import ApiResponse from '../models/api/ApiResponse';
import { GetLanguageInUrl, IsLaguagePresentInUrl } from '../utils/urls';

/**
 * MIME de un fichero XLSX (Open XML Spreadsheet).
 */
export const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Milisegundos que esperamos antes de liberar la URL del objeto. Si se revoca de forma
 * síncrona justo después del click algunos navegadores cancelan la descarga.
 */
const REVOKE_OBJECT_URL_DELAY: number = 250;

/**
 * Resultado de una descarga.
 * El backend puede responder con HTTP 200 y un JSON de error (por ejemplo cuando la
 * exportación supera el máximo de filas permitido), así que NO se puede usar
 * response.ok para saber si hay fichero: hay que olfatear el Content-Type.
 */
export interface DownloadFileResult {
    isFile: boolean,
    blob: Blob | null,
    json: ApiResponse | null,
    status: number,
}

/**
 * Mismas cabeceras que RestApiClient.getHeaders (restApiClient.tsx).
 * Sin 'Content-Type' porque la petición no lleva cuerpo.
 * OJO: 'Authority' es obligatoria, sin ella el backend no resuelve el tenant.
 */
const getHeaders = (): Headers => {
    const headers = new Headers();

    if (sessionStorage.getItem('token')) {
        headers.append('X-Authorization', `bearer ${sessionStorage.getItem('token')!}`);
    }

    const actualLanguage = IsLaguagePresentInUrl() ? GetLanguageInUrl().substring(1) : 'es';

    headers.append('X-user-app-language', actualLanguage);
    headers.append('Authority', window.location.host);

    return headers;
}

/**
 * Construye la URL del endpoint igual que RestApiClient.getUrlEndPoint, limpiando del
 * filtro los valores null / undefined / '' para que no acaben en la query como "undefined".
 */
export const buildDownloadUrl = (controller: string, actionName: string, query?: { [key: string]: any }): string => {
    const baseUrl = `${process.env.REACT_APP_BASE_API}/${process.env.REACT_APP_API_VERSION}/${controller}/${actionName}`;
    const urlSearchParams = new URLSearchParams();

    if (query) {
        for (const key in query) {
            const value = query[key];

            if (Array.isArray(value)) {
                value.forEach(item => {
                    if (item !== null && item !== undefined && String(item).trim() !== '') {
                        urlSearchParams.append(key, String(item));
                    }
                });
            } else if (value !== null && value !== undefined && String(value).trim() !== '') {
                urlSearchParams.append(key, String(value));
            }
        }
    }

    const queryString = urlSearchParams.toString();

    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * Descarga un fichero del API.
 *
 * No se puede reutilizar RestApiClient: hace response.json() sin condición y un cuerpo
 * binario revienta el parseo.
 */
export const downloadFile = async (controller: string, actionName: string, query?: { [key: string]: any }): Promise<DownloadFileResult> => {

    const response = await fetch(buildDownloadUrl(controller, actionName, query), {
        method: 'GET',
        headers: getHeaders(),
    });

    const contentType = response.headers.get('content-type') || '';

    if (contentType.indexOf(XLSX_MIME_TYPE) !== -1) {
        const blob = await response.blob();

        return { isFile: true, blob: blob, json: null, status: response.status };
    }

    let json: ApiResponse | null = null;

    try {
        json = await response.json();
    } catch (error) {
        console.error('downloadFile: la respuesta no es un XLSX ni un JSON válido', error);
    }

    return { isFile: false, blob: null, json: json, status: response.status };
}

/**
 * Dispara la descarga del blob en el navegador con el nombre indicado.
 * El nombre lo pone el front: Content-Disposition no está expuesta en el CORS del
 * backend, así que el navegador no puede leerla.
 */
export const saveBlobAsFile = (blob: Blob, fileName: string): void => {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = objectUrl;
    link.download = fileName;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Si no se libera, el blob se queda retenido en memoria hasta recargar la página.
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), REVOKE_OBJECT_URL_DELAY);
}
